import { GAME, BALANCE } from '../constants/index.js';
import {
  PlayerState, Upgrades, UpgradeTree, UpgradePurchaseRequest,
  UpgradePurchaseResult,
} from '../types/index.js';

export class UpgradeSystem {
  purchaseUpgrade(player: PlayerState, tree: string, level: number): boolean {
    const upgradeTree = tree as UpgradeTree;
    const currentLevel = player.upgrades[upgradeTree];

    if (level !== currentLevel + 1) return false;
    if (level > GAME.MAX_UPGRADE_LEVEL) return false;

    const cost = this.getUpgradeCost(upgradeTree, level);
    if (player.orbs < cost) return false;

    player.orbs -= cost;
    player.upgrades[upgradeTree] = level;

    return true;
  }

  processPurchase(player: PlayerState, request: UpgradePurchaseRequest): UpgradePurchaseResult {
    const stat = request.stat;
    const currentLevel = player.upgrades[stat];
    const reject = (reason: UpgradePurchaseResult['reason']): UpgradePurchaseResult => ({
      requestId: request.requestId,
      success: false,
      stat,
      newLevel: currentLevel,
      remainingOrbs: player.orbs,
      nextCost: this.getNextCost(stat, currentLevel),
      reason,
    });

    if (!(stat in player.upgrades) || request.expectedCurrentLevel !== currentLevel) return reject('invalid_level');
    if (currentLevel >= GAME.MAX_UPGRADE_LEVEL) return reject('max_level');
    const cost = this.getUpgradeCost(stat, currentLevel + 1);
    if (player.orbs < cost) return reject('insufficient_orbs');

    player.orbs -= cost;
    player.upgrades[stat] = currentLevel + 1;
    return {
      requestId: request.requestId,
      success: true,
      stat,
      newLevel: currentLevel + 1,
      remainingOrbs: player.orbs,
      nextCost: this.getNextCost(stat, currentLevel + 1),
    };
  }

  getUpgradeCost(tree: UpgradeTree, level: number): number {
    switch (tree) {
      case UpgradeTree.Push:
        return BALANCE.UPGRADE_COSTS[level as keyof typeof BALANCE.UPGRADE_COSTS] || 99;
      case UpgradeTree.Weight:
        return BALANCE.WEIGHT_UPGRADE_COSTS[level as keyof typeof BALANCE.WEIGHT_UPGRADE_COSTS] || 99;
      case UpgradeTree.Speed:
        return BALANCE.SPEED_UPGRADE_COSTS[level as keyof typeof BALANCE.SPEED_UPGRADE_COSTS] || 99;
      case UpgradeTree.Control:
        return BALANCE.UPGRADE_COSTS[level as keyof typeof BALANCE.UPGRADE_COSTS] || 99;
      default:
        return 99;
    }
  }

  getNextCost(tree: UpgradeTree, currentLevel: number): number | null {
    if (currentLevel >= GAME.MAX_UPGRADE_LEVEL) return null;
    return this.getUpgradeCost(tree, currentLevel + 1);
  }

  calculatePushForce(attackerUpgrades: Upgrades): number {
    return 1 + BALANCE.PUSH_POWER_PER_LEVEL * attackerUpgrades.push;
  }

  calculatePushResistance(targetUpgrades: Upgrades): number {
    return 1 - BALANCE.WEIGHT_PUSH_REDUCTION * targetUpgrades.weight;
  }

  calculateSpeedMultiplier(upgrades: Upgrades): number {
    const speedBonus = 1 + BALANCE.SPEED_UPGRADE_MULTIPLIER * upgrades.speed;
    const weightPenalty = 1 - BALANCE.WEIGHT_SPEED_PENALTY * upgrades.weight;
    return speedBonus * weightPenalty;
  }

  calculateControlMultiplier(upgrades: Upgrades): number {
    return 1 + BALANCE.CONTROL_EFFECT_PER_LEVEL * upgrades.control;
  }

  getTotalOrbsSpent(upgrades: Upgrades): number {
    let total = 0;
    for (const [tree, level] of Object.entries(upgrades)) {
      for (let lvl = 1; lvl <= level; lvl++) {
        total += this.getUpgradeCost(tree as UpgradeTree, lvl);
      }
    }
    return total;
  }
}